import React, { useState, useEffect, useMemo } from 'react';
import { ProLayout, PageContainer } from '@ant-design/pro-components';
import { Dropdown } from 'antd';
import {
  UserOutlined,
  MailOutlined,
  ShoppingOutlined,
  LogoutOutlined,
  AppstoreOutlined,
  DollarOutlined,
  RobotOutlined,
  TeamOutlined,
  BulbOutlined,
  ThunderboltOutlined,
  OrderedListOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  ReadOutlined,
  MessageOutlined,
  CrownOutlined,
  DatabaseOutlined,
  NotificationOutlined,
  PictureOutlined,
  LockOutlined,
  SendOutlined,
  BankOutlined,
  LineChartOutlined,
  BugOutlined,
  WarningOutlined,
  GiftOutlined,
  ShopOutlined,
  FileTextOutlined,
  CommentOutlined,
  WalletOutlined,
  SolutionOutlined,
  QuestionCircleOutlined,
  PlayCircleOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import AdminLanguageSwitcher from './common/AdminLanguageSwitcher';
import InvitationManager from './InvitationManager';
import RequirementManager from './RequirementManager';
import SMSTemplateManager from './SMSTemplateManager';
import SMSSettingsManager from './SMSSettingsManager';
import EmailSettingsManager from './EmailSettingsManager';
import UsersList from './UsersList';
import DevicesBlacklistManager from './DevicesBlacklistManager';
import FeatureRequestManager from './FeatureRequestManager';
import TrainManager from './TrainManager';
import GameStepsManager from './GameStepsManager';
import RegistrationSettingsManager from './RegistrationSettingsManager';
import LanguagesSettingsManager from './LanguagesSettingsManager';
import AiSettingsManager from './AiSettingsManager';
import YearGameManager from './YearGameManager';
import BlogManager from './BlogManager';
import WaitlistManager from './WaitlistManager';
import PersonsManager from './PersonsManager';
import TrainScheduleManager from './TrainScheduleManager';
import AdminNotificationsBell from './AdminNotificationsBell';
import BackupManager from './BackupManager';
import TeachersManager from './TeachersManager';
import SiteMessagesManager from './SiteMessagesManager';
import LandingContentManager from './LandingContentManager';
import AboutContentManager from './AboutContentManager';
import FAQManager from './FAQManager';
import JourneyVideoManager from './JourneyVideoManager';
import SiteNavManager from './SiteNavManager';
import PaymentSettingsManager from './PaymentSettingsManager';
import TelegramBotSettingsManager from './TelegramBotSettingsManager';
import BirthdayGreetingManager from './BirthdayGreetingManager';
import ChangePasswordManager from './ChangePasswordManager';
import ContactMessageManager from './ContactMessageManager';
import SupportTicketManager from './SupportTicketManager';
import GringottsBankManager from './GringottsBankManager';
import DashboardOverview from './DashboardOverview';
import AnalyticsManager from './AnalyticsManager';
import ErrorMonitor from './ErrorMonitor';
import SecurityLogManager from './SecurityLogManager';
import AnalyticsTrackingManager from './AnalyticsTrackingManager';
import RequestLogsManager from './RequestLogsManager';
import TestResultManager from './TestResultManager';
import ChatLogsManager from './ChatLogsManager';
import GameMissionManager from './GameMissionManager';
import ForumModerationManager from './ForumModerationManager';
import TransactionLogManager from './TransactionLogManager';
import SMSLogManager from './SMSLogManager';
import DiagonAlleyManager from './DiagonAlleyManager';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [activeKey, setActiveKey] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) return hash;
    const saved = localStorage.getItem('admin_active_tab');
    if (saved) return saved;
    return null;
  });

  // خواندن نقش از کوکی
  const adminCookie = useMemo(() => {
    try { return JSON.parse(Cookies.get('admin') || '{}'); } catch { return {}; }
  }, []);
  const role = adminCookie.role || 'admin';
  const isTeacher = role === 'teacher';
  const defaultActiveKey = isTeacher ? 'waitlist' : 'dashboard';

  useEffect(() => {
    if (!activeKey) {
      setActiveKey(defaultActiveKey);
      localStorage.setItem('admin_active_tab', defaultActiveKey);
      window.location.hash = defaultActiveKey;
    }
  }, [activeKey, defaultActiveKey]);

  const handleLogout = () => {
    Cookies.remove('token');
    Cookies.remove('admin');
    localStorage.removeItem('admin_active_tab');
    window.location.href = '/admin/login';
  };

  const handleTabChange = (key) => {
    setActiveKey(key);
    localStorage.setItem('admin_active_tab', key);
    window.location.hash = key;
  };

  // ساختار درختی و گروه‌بندی شده آیتم‌های منو — بازطراحی شده به هشت گروه
  // موضوعی منسجم (به‌جای ۴ آیتم شناور + ۵ گروه قدیمی). هر آیتم برگ دقیقاً
  // یک بار در switch محتوا مصرف می‌شود.
  const mi = (key) => t(`admin.AdminDashboard.items.${key}`);
  const mg = (key) => t(`admin.AdminDashboard.groups.${key}`);

  const allMenuItems = [
    {
      key: 'dashboard-group',
      icon: <AppstoreOutlined />,
      label: mg('dashboard-group'),
      roles: ['admin'],
      routes: [
        { key: 'dashboard',        icon: <AppstoreOutlined />,  label: mi('dashboard'),           roles: ['admin'] },
        { key: 'analytics',        icon: <LineChartOutlined />, label: mi('analytics'),  roles: ['admin'] },
        { key: 'security-logs',    icon: <WarningOutlined />,   label: mi('security-logs'),   roles: ['admin'] },
      ]
    },

    {
      key: 'users-group',
      icon: <TeamOutlined />,
      label: mg('users-group'),
      roles: ['admin', 'teacher'],
      routes: [
        { key: 'users',                 icon: <UserOutlined />,              label: mi('users'),              roles: ['admin', 'teacher'] },
        { key: 'waitlist',              icon: <TeamOutlined />,              label: mi('waitlist'),          roles: ['admin', 'teacher'] },
        { key: 'invitations',           icon: <MailOutlined />,              label: mi('invitations'),          roles: ['admin', 'teacher'] },
        { key: 'teachers',              icon: <CrownOutlined />,             label: mi('teachers'),       roles: ['admin'] },
        { key: 'devices-blacklist',     icon: <LockOutlined />,              label: mi('devices-blacklist'),  roles: ['admin'] },
        { key: 'change-password',       icon: <LockOutlined />,              label: mi('change-password'),       roles: ['admin', 'teacher'] },
      ]
    },

    {
      key: 'game-group',
      icon: <StarOutlined />,
      label: mg('game-group'),
      roles: ['admin', 'teacher'],
      routes: [
        { key: 'game-steps',            icon: <OrderedListOutlined />,       label: mi('game-steps'),           roles: ['admin'] },
        { key: 'train',                 icon: <ThunderboltOutlined />,       label: mi('train'),       roles: ['admin'] },
        { key: 'train-schedules',       icon: <ThunderboltOutlined />,       label: mi('train-schedules'),   roles: ['admin', 'teacher'] },
        { key: 'requirements',          icon: <ShoppingOutlined />,          label: mi('requirements'),         roles: ['admin'] },
        { key: 'year-game',             icon: <StarOutlined />,              label: mi('year-game'),          roles: ['admin', 'teacher'] },
        { key: 'game-missions',         icon: <SolutionOutlined />,          label: mi('game-missions'), roles: ['admin'] },
        { key: 'persons',               icon: <TeamOutlined />,              label: mi('persons'),        roles: ['admin', 'teacher'] },
        { key: 'test-results',          icon: <SafetyCertificateOutlined />, label: mi('test-results'),        roles: ['admin'] },
      ]
    },

    {
      key: 'diagon-alley-group',
      icon: <ShopOutlined />,
      label: mg('diagon-alley-group'),
      roles: ['admin'],
      routes: [
        { key: 'diagon-alley',          icon: <ShopOutlined />,              label: mi('diagon-alley'),   roles: ['admin'] },
      ]
    },

    {
      key: 'content-group',
      icon: <PictureOutlined />,
      label: mg('content-group'),
      roles: ['admin'],
      routes: [
        { key: 'landing-content',       icon: <PictureOutlined />,           label: mi('landing-content'),     roles: ['admin'] },
        { key: 'about-content',         icon: <PictureOutlined />,           label: mi('about-content'), roles: ['admin'] },
        { key: 'faq',                   icon: <QuestionCircleOutlined />,    label: mi('faq'),  roles: ['admin'] },
        { key: 'journey-video',         icon: <PlayCircleOutlined />,        label: mi('journey-video'),    roles: ['admin'] },
        { key: 'site-nav',              icon: <AppstoreOutlined />,          label: mi('site-nav'),       roles: ['admin'] },
        { key: 'sms-templates',         icon: <MessageOutlined />,           label: mi('sms-templates'),          roles: ['admin'] },
        { key: 'site-messages',         icon: <NotificationOutlined />,      label: mi('site-messages'),        roles: ['admin'] },
        { key: 'blog',                  icon: <ReadOutlined />,              label: mi('blog'),       roles: ['admin'] },
        { key: 'forum-moderation',      icon: <CommentOutlined />,           label: mi('forum-moderation'),       roles: ['admin'] },
      ]
    },

    {
      key: 'support-group',
      icon: <MailOutlined />,
      label: mg('support-group'),
      roles: ['admin', 'teacher'],
      routes: [
        { key: 'support-tickets',       icon: <MailOutlined />,              label: mi('support-tickets'), roles: ['admin', 'teacher'] },
        { key: 'contact-messages',      icon: <MailOutlined />,              label: mi('contact-messages'),         roles: ['admin'] },
        { key: 'feature-requests',      icon: <BulbOutlined />,              label: mi('feature-requests'),   roles: ['admin'] },
      ]
    },

    {
      key: 'monitoring-group',
      icon: <BugOutlined />,
      label: mg('monitoring-group'),
      roles: ['admin'],
      routes: [
        { key: 'error-monitor',         icon: <BugOutlined />,               label: mi('error-monitor'),     roles: ['admin'] },
        { key: 'request-logs',          icon: <OrderedListOutlined />,       label: mi('request-logs'),      roles: ['admin'] },
        { key: 'gringotts-bank',        icon: <BankOutlined />,              label: mi('gringotts-bank'),        roles: ['admin'] },
        { key: 'chat-logs',             icon: <RobotOutlined />,             label: mi('chat-logs'),    roles: ['admin'] },
        { key: 'transactions',          icon: <WalletOutlined />,            label: mi('transactions'),       roles: ['admin'] },
        { key: 'sms-logs',              icon: <FileTextOutlined />,          label: mi('sms-logs'),         roles: ['admin'] },
      ]
    },

    {
      key: 'system-group',
      icon: <DatabaseOutlined />,
      label: mg('system-group'),
      roles: ['admin'],
      routes: [
        { key: 'registration-settings', icon: <SafetyCertificateOutlined />, label: mi('registration-settings'),        roles: ['admin'] },
        { key: 'languages-settings',    icon: <GlobalOutlined />,            label: mi('languages-settings'), roles: ['admin'] },
        { key: 'ai-settings',           icon: <RobotOutlined />,             label: mi('ai-settings'),  roles: ['admin'] },
        { key: 'payment-settings',      icon: <DollarOutlined />,            label: mi('payment-settings'),       roles: ['admin'] },
        { key: 'sms-settings',          icon: <MessageOutlined />,           label: mi('sms-settings'),          roles: ['admin'] },
        { key: 'email-settings',        icon: <MailOutlined />,              label: mi('email-settings'),        roles: ['admin'] },
        { key: 'telegram-bot',          icon: <SendOutlined />,              label: mi('telegram-bot'),          roles: ['admin'] },
        { key: 'birthday-settings',      icon: <GiftOutlined />,              label: mi('birthday-settings'),    roles: ['admin'] },
        { key: 'backup',                icon: <DatabaseOutlined />,          label: mi('backup'),         roles: ['admin'] },
        { key: 'analytics-tracking',    icon: <LineChartOutlined />,         label: mi('analytics-tracking'), roles: ['admin'] },
      ]
    }
  ];

  // فیلتر منوها بر اساس نقش کاربر
  const filterMenuItems = (items, userRole) => {
    return items
      .filter(item => !item.roles || item.roles.includes(userRole))
      .map(item => {
        if (item.routes) {
          const filteredRoutes = item.routes.filter(r => !r.roles || r.roles.includes(userRole));
          return { ...item, routes: filteredRoutes };
        }
        return item;
      })
      .filter(item => !item.routes || item.routes.length > 0);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const menuItems = useMemo(() => filterMenuItems(allMenuItems, role), [role]);

  // لیست مسطح (Flat) همه آیتم‌های معتبر برگ برای پیدا کردن عنوان فعال
  const flatMenuItems = useMemo(() => {
    const list = [];
    menuItems.forEach(item => {
      if (item.routes) {
        list.push(...item.routes);
      } else {
        list.push(item);
      }
    });
    return list;
  }, [menuItems]);

  useEffect(() => {
    if (!activeKey || flatMenuItems.length === 0) return;
    if (!flatMenuItems.some(item => item.key === activeKey)) {
      setActiveKey(defaultActiveKey);
      localStorage.setItem('admin_active_tab', defaultActiveKey);
      window.location.hash = defaultActiveKey;
    }
  }, [activeKey, flatMenuItems, defaultActiveKey]);

  const currentItem = flatMenuItems.find(m => m.key === activeKey);

  // تا وقتی نقش/تب اولیه مشخص نشده چیزی رندر نکن
  if (!activeKey) return null;

  return (
    <ProLayout
      title={t('admin.AdminDashboard.appTitle')}
      logo={<span style={{ fontSize: 22 }}>🪄</span>}
      layout="mix"
      fixSiderbar
      collapsed={collapsed}
      onCollapse={setCollapsed}
      location={{ pathname: `/${activeKey}` }}
      route={{
        path: '/',
        routes: menuItems.map(item => {
          if (item.routes) {
            return {
              path: `/${item.key}`,
              name: item.label,
              icon: item.icon,
              routes: item.routes.map(sub => ({
                path: `/${sub.key}`,
                name: sub.label,
                icon: sub.icon,
              }))
            };
          }
          return {
            path: `/${item.key}`,
            name: item.label,
            icon: item.icon,
          };
        }),
      }}
      menuItemRender={(item, dom) => {
        // فقط روی آیتم‌های نهایی کلیک کار کند (نه دسته‌ها که به -group ختم می‌شوند)
        if (item.path && !item.path.endsWith('-group')) {
          return <div onClick={() => handleTabChange(item.path.replace('/', ''))}>{dom}</div>;
        }
        return dom;
      }}
      avatarProps={{
        icon: <UserOutlined />,
        title: isTeacher ? t('admin.AdminDashboard.teacherRole') : t('admin.AdminDashboard.adminRole'),
        render: (_props, dom) => (
          <Dropdown
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: t('admin.AdminDashboard.logout'),
                  danger: true,
                  onClick: handleLogout,
                },
              ],
            }}
          >
            {dom}
          </Dropdown>
        ),
      }}
      actionsRender={() => [
        <AdminLanguageSwitcher key="lang" />,
        ...(isTeacher ? [] : [<AdminNotificationsBell key="bell" />]),
      ]}
      token={{
        sider: {
          colorMenuBackground: '#1a1a2e',
          colorBgMenuItemSelected: '#722f37',
          colorBgMenuItemHover: 'rgba(212,175,55,0.10)',
          colorTextMenu: 'rgba(255,255,255,0.75)',
          colorTextMenuSelected: '#ffffff',
          colorTextMenuActive: '#f5d77d',
          colorTextMenuItemHover: '#f5d77d',
        },
        header: {
          colorBgHeader: '#ffffff',
          colorHeaderTitle: '#1a1a2e',
        },
      }}
    >
      <PageContainer
        title={currentItem?.label || t('admin.AdminDashboard.dashboardFallback')}
        extra={!isTeacher ? [
          <span key="brand" style={{ color: '#888', fontSize: 13 }}>
            <CrownOutlined style={{ marginInlineEnd: 4, color: '#722f37' }} />
            {t('admin.AdminDashboard.brand')}
          </span>
        ] : []}
        ghost
      >
        {activeKey === 'dashboard' && !isTeacher && <DashboardOverview />}
        {activeKey === 'analytics' && <AnalyticsManager />}
        {activeKey === 'security-logs' && <SecurityLogManager />}
        {activeKey === 'registration-settings' && <RegistrationSettingsManager />}
        {activeKey === 'languages-settings' && <LanguagesSettingsManager />}
        {activeKey === 'game-steps' && <GameStepsManager />}
        {activeKey === 'invitations' && <InvitationManager />}
        {activeKey === 'waitlist' && <WaitlistManager />}
        {activeKey === 'requirements' && <RequirementManager />}
        {activeKey === 'sms-settings' && <SMSSettingsManager />}
        {activeKey === 'email-settings' && <EmailSettingsManager />}
        {activeKey === 'sms-templates' && <SMSTemplateManager />}
        {activeKey === 'birthday-settings' && <BirthdayGreetingManager />}
        {activeKey === 'site-messages' && <SiteMessagesManager />}
        {activeKey === 'landing-content' && <LandingContentManager />}
        {activeKey === 'about-content' && <AboutContentManager />}
        {activeKey === 'faq' && <FAQManager />}
        {activeKey === 'journey-video' && <JourneyVideoManager />}
        {activeKey === 'site-nav' && <SiteNavManager />}
        {activeKey === 'payment-settings' && <PaymentSettingsManager />}
        {activeKey === 'telegram-bot' && <TelegramBotSettingsManager />}
        {activeKey === 'change-password' && <ChangePasswordManager />}
        {activeKey === 'devices-blacklist' && <DevicesBlacklistManager />}
        {activeKey === 'users' && <UsersList />}
        {activeKey === 'persons' && <PersonsManager />}
        {activeKey === 'train-schedules' && <TrainScheduleManager />}
        {activeKey === 'ai-settings' && <AiSettingsManager />}
        {activeKey === 'train' && <TrainManager />}
        {activeKey === 'year-game' && <YearGameManager role={role} />}
        {activeKey === 'blog' && <BlogManager />}
        {activeKey === 'feature-requests' && <FeatureRequestManager />}
        {activeKey === 'contact-messages' && <ContactMessageManager />}
        {activeKey === 'support-tickets' && <SupportTicketManager />}
        {activeKey === 'gringotts-bank' && <GringottsBankManager />}
        {activeKey === 'teachers' && <TeachersManager />}
        {activeKey === 'backup' && <BackupManager />}
        {activeKey === 'error-monitor' && <ErrorMonitor />}
        {activeKey === 'request-logs' && <RequestLogsManager />}
        {activeKey === 'analytics-tracking' && <AnalyticsTrackingManager />}
        {activeKey === 'test-results' && <TestResultManager />}
        {activeKey === 'chat-logs' && <ChatLogsManager />}
        {activeKey === 'game-missions' && <GameMissionManager />}
        {activeKey === 'forum-moderation' && <ForumModerationManager />}
        {activeKey === 'transactions' && <TransactionLogManager />}
        {activeKey === 'sms-logs' && <SMSLogManager />}
        {activeKey === 'diagon-alley' && <DiagonAlleyManager />}
      </PageContainer>
    </ProLayout>
  );
}
